import React from 'react';
import { Pin, Trash2, Share2, FolderInput } from 'lucide-react';

export default function NotesList({ notes = [], selectedNote, onSelectNote, onDeleteNote, onTogglePin, onShareNote, onMoveNote, loading, theme, isSharedView }) {
  if (loading) {
    return (
      <div className="p-3 space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className={`h-20 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'}`} />
        ))}
      </div>
    );
  }

  if (!notes || notes.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-500 text-xs font-rasa">No notes yet</p>
      </div>
    );
  }

  return (
    <div>
      {notes.map(note => {
        if (!note || !note.id) return null;
        return (
          <div
            key={note.id}
            onClick={() => onSelectNote(note)}
            className={`group px-4 py-3 cursor-pointer border-b transition-colors ${
              selectedNote?.id === note.id
                ? theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'
                : theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
            } ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {note.color && note.color !== '#FFFFFF' && (
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: note.color }} />
                  )}
                  <h3 className="font-unna font-medium text-sm truncate">
                    {note.title || 'Untitled'}
                  </h3>
                  {note.pinned && <Pin size={10} className="text-orange-500 fill-orange-500 flex-shrink-0" />}
                </div>
                <p className={`text-xs font-rasa mt-1 line-clamp-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {(note.content || '').replace(/<[^>]+>/g, '').replace(/[#*_~`>\-|]/g, '').substring(0, 100) || 'Empty note'}
                </p>
                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                  {note.category && note.category.split(',').map(c => c.trim()).filter(Boolean).slice(0, 3).map(cat => (
                    <span key={cat} className={`text-[10px] font-rasa px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                      {cat}
                    </span>
                  ))}
                  {note.folder_name && (
                    <span className={`text-[10px] font-rasa px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                      {note.folder_name}
                    </span>
                  )}
                  {isSharedView && note.owner && (
                    <span className={`text-[10px] font-rasa ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                      by {note.owner.username}
                    </span>
                  )}
                  {(note.shared_count || 0) > 0 && !isSharedView && (
                    <Share2 size={10} className="text-blue-500" />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Move to folder — available for owned and shared notes */}
                {onMoveNote && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onMoveNote(note); }}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400"
                    title="Move to folder"
                  >
                    <FolderInput size={12} />
                  </button>
                )}
                {/* Owner-only actions */}
                {!isSharedView && note.is_owner && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); onTogglePin(note); }}
                      className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${note.pinned ? 'text-orange-500' : 'text-gray-400'}`}
                      title={note.pinned ? 'Unpin' : 'Pin'}
                    >
                      <Pin size={12} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onShareNote(note); }}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400"
                      title="Share"
                    >
                      <Share2 size={12} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteNote(note.id); }}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-red-400"
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
